import { Popover } from '@mui/material'
import clsx from 'clsx'
import { useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import { CalendarProps } from 'react-multi-date-picker'
import { ClassName } from 'types/props'

import { Calendar } from './calendar'
import { ClearDatesButton } from './clear-dates-button'
import { DisplayedValue } from './displayed-value'
import classes from './index.module.scss'
import { type SpeedDial, type SpeedDialRange, SpeedDialSection } from './speed-dial-section'
import { areDateRangesEqual } from './utilities'

export * from './common-speed-dials'

export type Value = Date | [Date, Date]
interface BaseProps<T extends Value> extends CalendarProps, ClassName {
  alignPopover?: 'left' | 'center' | 'right'
  closeOnClick?: boolean
  customTriggerElement?: JSX.Element
  defaultValue: T
  id: string
  maxSelectableDate?: Date
  showClearDatesButton: boolean
  speedDials?: SpeedDial[]
  variant: 'single' | 'range'
}

export interface BodyProps<T extends Value> extends BaseProps<T> {
  isOnlyMonthPicker?: boolean
  onChange: (value: any) => void
  value: T | null
}
const PopoverBody = <T extends Value>({
  alignPopover = 'left',
  className,
  closeOnClick = false,
  customTriggerElement,
  defaultValue,
  id,
  isOnlyMonthPicker,
  maxSelectableDate,
  onChange,
  showClearDatesButton,
  speedDials,
  value,
  variant,
  ...calendarProps
}: BodyProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef(null)

  const closeMenu = () => setIsOpen(false)
  const toggleMenu = () => setIsOpen(isOpen => !isOpen)

  const defaultIsSelected =
    variant === 'range' && areDateRangesEqual(value as [Date, Date], defaultValue as [Date, Date])

  return (
    <div className={clsx(classes.body, className)}>
      <div className={classes.topWrapper}>
        <button
          ref={buttonRef}
          aria-controls={id}
          aria-haspopup="true"
          className={clsx({
            [classes.displayedValueButton]: !customTriggerElement,
            [classes.customTriggerElement]: customTriggerElement
          })}
          id={`${id}-button`}
          type="button"
          onClick={toggleMenu}
        >
          {customTriggerElement || <DisplayedValue value={value} />}
        </button>
        {showClearDatesButton && !defaultIsSelected && (
          <ClearDatesButton defaultValue={defaultValue as [Date, Date]} onChange={onChange} />
        )}
      </div>

      <Popover
        keepMounted
        anchorEl={buttonRef?.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: alignPopover
        }}
        aria-labelledby={`${id}-button`}
        classes={{ paper: clsx(classes.popover, { [classes.popoverOpen]: isOpen }) }}
        id={id}
        open={isOpen}
        role="menu"
        transformOrigin={{
          vertical: 'top',
          horizontal: alignPopover
        }}
        onClose={closeMenu}
      >
        <div className={clsx(classes.calendarWrapper, classes.shadow)}>
          {speedDials && (
            <SpeedDialSection
              speedDials={speedDials}
              value={value}
              variant={variant}
              onChange={onChange}
            />
          )}
          <Calendar<T>
            isOnlyMonthPicker={isOnlyMonthPicker}
            maxSelectableDate={maxSelectableDate}
            value={value}
            variant={variant}
            onChange={value => {
              if (closeOnClick) closeMenu()
              onChange(value)
            }}
            {...calendarProps}
          />
        </div>
      </Popover>
    </div>
  )
}

const Body = <T extends Value>({
  className,
  defaultValue,
  isOnlyMonthPicker,
  maxSelectableDate,
  onChange,
  showClearDatesButton,
  speedDials,
  value,
  variant,
  ...calendarProps
}: BodyProps<T>) => (
  <div className={clsx(classes.body, className)}>
    <div className={classes.topWrapper}>
      <DisplayedValue value={value} />
      {showClearDatesButton && (
        <ClearDatesButton defaultValue={defaultValue as [Date, Date]} onChange={onChange} />
      )}
    </div>
    <div className={classes.calendarWrapper}>
      {speedDials && (
        <SpeedDialSection
          speedDials={speedDials}
          value={value}
          variant={variant}
          onChange={onChange}
        />
      )}
      <Calendar<T>
        isOnlyMonthPicker={isOnlyMonthPicker}
        maxSelectableDate={maxSelectableDate}
        value={value}
        variant={variant}
        onChange={onChange}
        {...calendarProps}
      />
    </div>
  </div>
)

interface Props<T extends Value> extends BaseProps<T> {
  isOnlyMonthPicker?: boolean
  isPopover: boolean
}

const Picker = <T extends Value>({
  defaultValue,
  id,
  isPopover,
  showClearDatesButton = true,
  ...calendarProps
}: Props<T>) => (
  <Controller
    defaultValue={defaultValue}
    name={id}
    render={({ field: { onChange, value } }) =>
      isPopover ? (
        <PopoverBody
          defaultValue={defaultValue}
          id={id}
          showClearDatesButton={showClearDatesButton}
          value={value}
          onChange={onChange}
          {...calendarProps}
        />
      ) : (
        <Body
          defaultValue={defaultValue}
          id={id}
          showClearDatesButton={showClearDatesButton}
          value={value}
          onChange={onChange}
          {...calendarProps}
        />
      )
    }
  />
)

export const DateRangePicker = (props: Omit<Props<[Date, Date]>, 'variant'>) => (
  <Picker<[Date, Date]> variant="range" {...props} />
)
export const DatePicker = (props: Omit<Props<Date>, 'variant'>) => (
  <Picker<Date> showClearDatesButton={false} variant="single" {...props} />
)

export { SpeedDial, SpeedDialSection }

export type { SpeedDialRange }
